import React from "react";
import { Link } from "react-router-dom";
import {
  Shield,
  Lock,
  Eye,
  Server,
  Users,
  CheckCircle,
  ArrowRight,
  FileCheck,
  Globe,
  Key,
  AlertTriangle,
  Smartphone,
} from "lucide-react";

const Security: React.FC = () => {
  const securityFeatures = [
    {
      icon: Lock,
      title: "End-to-End Encryption",
      description:
        "All data is encrypted in transit and at rest using AES-256 encryption, ensuring your sensitive information is always protected.",
    },
    {
      icon: Shield,
      title: "SOC 2 Type II Compliant",
      description:
        "Independently audited and certified for security, availability, processing integrity, confidentiality, and privacy.",
    },
    {
      icon: Globe,
      title: "GDPR Compliant",
      description:
        "Full compliance with European data protection regulations, giving you and your candidates complete control over personal data.",
    },
    {
      icon: Key,
      title: "Multi-Factor Authentication",
      description:
        "Enhanced account security with MFA support, including SMS, email, and authenticator app options.",
    },
    {
      icon: Eye,
      title: "Advanced Access Controls",
      description:
        "Role-based permissions and audit trails ensure only authorized personnel can access sensitive candidate information.",
    },
    {
      icon: Server,
      title: "Infrastructure Security",
      description:
        "Hosted on enterprise-grade cloud infrastructure with 24/7 monitoring, DDoS protection, and automatic backups.",
    },
  ];

  const certifications = [
    {
      name: "SOC 2 Type II",
      description: "Audited annually for security controls",
      icon: FileCheck,
    },
    {
      name: "GDPR Compliant",
      description: "European data protection compliance",
      icon: Shield,
    },
    {
      name: "ISO 27001",
      description: "Information security management",
      icon: Lock,
    },
    {
      name: "CCPA Compliant",
      description: "California privacy law compliance",
      icon: Users,
    },
  ];

  const dataProtection = [
    "All candidate data is encrypted using industry-standard AES-256 encryption",
    "Data is automatically backed up every 24 hours with 99.9% uptime guarantee",
    "Geographic data redundancy across multiple secure data centers",
    "Complete data portability - export your data anytime in standard formats",
    "Data retention policies that automatically purge old data per your settings",
    "Secure data destruction when accounts are closed",
  ];

  const privacyPrinciples = [
    {
      title: "Data Minimization",
      description:
        "We only collect data that's necessary for providing our recruitment services.",
    },
    {
      title: "Purpose Limitation",
      description:
        "Data is used only for the specific purposes you've authorized.",
    },
    {
      title: "Consent Management",
      description:
        "Clear consent mechanisms for candidates with easy opt-out options.",
    },
    {
      title: "Transparency",
      description:
        "Full visibility into how data is collected, processed, and stored.",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Link to="/" className="flex items-center space-x-3">
              <img
                src="/vetriaAI.png"
                alt="VetriaAI Logo"
                className="w-10 h-10"
              />
              <span className="text-2xl font-bold text-gray-900">VetriaAI</span>
            </Link>
          </div>

          <div className="flex items-center space-x-4">
            <Link
              to="/login"
              className="text-gray-600 hover:text-gray-900 font-medium"
            >
              Sign In
            </Link>
            <Link
              to="/login"
              className="bg-red-800 text-white px-6 py-2 rounded-lg hover:bg-red-900 transition-colors font-medium"
            >
              Sign In
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-50 to-gray-100">
        <div className="max-w-7xl mx-auto text-center">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-10 h-10 text-blue-600" />
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Enterprise-Grade
            <span className="text-blue-600"> Security</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Your data security is our top priority. VetriaAI implements
            industry-leading security measures and compliance standards to
            protect your sensitive recruitment data.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/login"
              className="bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg inline-flex items-center justify-center"
            >
              Sign In <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/features"
              className="border border-gray-300 text-gray-700 px-8 py-4 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-lg"
            >
              View All Features
            </Link>
          </div>
        </div>
      </section>

      {/* Security Features */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Security Framework
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Multiple layers of protection to ensure your recruitment data
              stays secure and compliant
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {securityFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-shadow border border-gray-100"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-gray-50 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Industry Certifications & Compliance
            </h2>
            <p className="text-xl text-gray-600">
              Independently verified security standards and regulatory
              compliance
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {certifications.map((cert, index) => {
              const Icon = cert.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl shadow-sm text-center border border-gray-100"
                >
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {cert.name}
                  </h3>
                  <p className="text-gray-600 text-sm">{cert.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Data Protection */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Data Protection & Privacy
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                We implement comprehensive data protection measures to ensure
                your candidate information is secure, private, and handled with
                the utmost care.
              </p>
              <ul className="space-y-4">
                {dataProtection.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-8 rounded-2xl">
              <div className="text-center">
                <Server className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                  99.9% Uptime Guarantee
                </h3>
                <p className="text-gray-600 mb-6">
                  Enterprise-grade infrastructure with automatic failover and
                  24/7 monitoring ensures your recruitment process never stops.
                </p>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="bg-white rounded-lg p-4">
                    <div className="text-2xl font-bold text-blue-600">24/7</div>
                    <div className="text-sm text-gray-600">Monitoring</div>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <div className="text-2xl font-bold text-green-600">
                      99.9%
                    </div>
                    <div className="text-sm text-gray-600">Uptime</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Principles */}
      <section className="py-20 bg-gray-50 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Privacy by Design
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Privacy and data protection are built into every aspect of our
              platform
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {privacyPrinciples.map((principle, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl shadow-sm border border-gray-100"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {principle.title}
                </h3>
                <p className="text-gray-600">{principle.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Incident Response */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-8">
            <div className="flex items-start">
              <AlertTriangle className="w-8 h-8 text-yellow-600 mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                  Security Incident Response
                </h3>
                <p className="text-gray-700 mb-4">
                  In the unlikely event of a security incident, we have
                  established protocols to:
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li>• Immediately contain and assess the situation</li>
                  <li>• Notify affected customers within 24 hours</li>
                  <li>
                    • Work with law enforcement and security experts as needed
                  </li>
                  <li>
                    • Provide regular updates throughout the investigation
                  </li>
                  <li>
                    • Implement additional safeguards to prevent future
                    incidents
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 px-6">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl font-bold mb-4">Security You Can Trust</h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of companies who trust VetriaAI with their most
            sensitive recruitment data.
          </p>
          <Link
            to="/login"
            className="bg-white text-blue-600 px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors font-semibold text-lg inline-flex items-center"
          >
            Sign In <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-5 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img
                  src="/vetriaAI.png"
                  alt="VetriaAI Logo"
                  className="w-8 h-8"
                />
                <span className="text-xl font-bold">VetriaAI</span>
              </div>
              <p className="text-gray-400">
                Revolutionizing recruitment with AI-powered intelligence.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-red-900">Product</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/features" className="text-white hover:text-gray-300">
                    Features
                  </Link>
                </li>
                <li>
                  <Link to="/security" className="text-white hover:text-gray-300">
                    Security
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-red-900">Company</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/about" className="text-white hover:text-gray-300">
                    About
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-white hover:text-gray-300">
                    Contact
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-red-900">Support</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/help-center" className="text-white hover:text-gray-300">
                    Help Center
                  </Link>
                </li>
                <li>
                  <Link to="/status" className="text-white hover:text-gray-300">
                    Status
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-red-900">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/cancellations-refunds" className="text-white hover:text-gray-300">
                    Cancellations &amp; Refunds
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-white hover:text-gray-300">
                    Terms &amp; Conditions
                  </Link>
                </li>
                <li>
                  <Link to="/shipping" className="text-white hover:text-gray-300">
                    Shipping (Digital)
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-white hover:text-gray-300">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>2025 VetriaAI. All rights and trademarks are reserved by VetriaAI.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Security;
